import {
  EMBED_MAP_ATTRIB_FALLBACK_PX,
  EMBED_MAP_FIT_MARGIN_PX,
  EMBED_MAP_TOOLBAR_FALLBACK_PX,
  embedStripFitPadding,
} from "../shared/map-embed-padding.ts";
import type { Insets } from "../shared/rotated-fit.ts";

function visible(el: HTMLElement): boolean {
  if (el.hidden) return false;
  const style = getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden") return false;
  return el.getClientRects().length > 0;
}

function reserveBelow(mapTop: number, el: HTMLElement | null): number {
  if (!el || !visible(el)) return 0;
  const r = el.getBoundingClientRect();
  if (r.height <= 0) return 0;
  return Math.max(0, r.bottom - mapTop);
}

function reserveAbove(mapBottom: number, el: HTMLElement | null): number {
  if (!el || !visible(el)) return 0;
  const r = el.getBoundingClientRect();
  if (r.height <= 0) return 0;
  return Math.max(0, mapBottom - r.top);
}

/** Padding for embedded maps: one top toolbar strip, bottom attribution, and side margins. */
export function embedMapFitInsets(mapEl: HTMLElement, extraTop: readonly HTMLElement[] = []): Insets {
  const map = mapEl.getBoundingClientRect();
  const w = map.width;
  const h = map.height;
  if (w <= 0 || h <= 0) {
    return embedStripFitPadding(EMBED_MAP_TOOLBAR_FALLBACK_PX, EMBED_MAP_ATTRIB_FALLBACK_PX);
  }

  const toolbar = mapEl.querySelector<HTMLElement>(".route-map-toolbar");
  let top = toolbar ? reserveBelow(map.top, toolbar) : EMBED_MAP_TOOLBAR_FALLBACK_PX;
  for (const el of extraTop) {
    top = Math.max(top, reserveBelow(map.top, el));
  }
  if (top <= 0) top = EMBED_MAP_TOOLBAR_FALLBACK_PX;

  const credit = mapEl.querySelector<HTMLElement>(".route-map-credit, .maplibregl-ctrl-attrib");
  let bottom = credit ? reserveAbove(map.bottom, credit) : EMBED_MAP_ATTRIB_FALLBACK_PX;
  if (bottom <= 0) bottom = EMBED_MAP_ATTRIB_FALLBACK_PX;

  return embedStripFitPadding(top, bottom, EMBED_MAP_FIT_MARGIN_PX);
}
